import { useState } from 'react';
import { ArrowLeft, HelpCircle, Video, MessageSquare, Loader2, FileText, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';

export default function HelpSupport() {
  const navigate = useNavigate();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackMessage.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setLoading(true);
    try {
      // In production, call API
      toast.success('Thank you for your feedback!');
      setFeedbackMessage('');
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: 'Can I rename my organization?',
      answer: '⚠️ An organization, once created, cannot be renamed.',
      warning: true
    },
    {
      question: 'How do wedding credits work?',
      answer: 'Each credit allows you to create and manage one wedding. Credits are added with your subscription plan.'
    },
    {
      question: 'How do I invite team members?',
      answer: 'Navigate to Team Members from your profile menu, then click "Invite Member" and enter their email address.'
    },
    {
      question: 'Can I export my wedding data?',
      answer: 'Yes! You can export guest lists, budgets, and timelines from each wedding\'s detail page.'
    },
    {
      question: 'What happens if I exceed my wedding limit?',
      answer: 'You can upgrade your plan anytime from My Subscription to get more wedding credits.'
    }
  ];

  const getTodayDate = () => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-background">
      <EnhancedAppHeader />
      
      <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text">Help & Support</h1>
            <p className="text-sm text-muted-foreground mt-1">Get help, send feedback, and review our policies</p>
          </div>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {/* Help & FAQ Section */}
          <AccordionItem value="help" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <HelpCircle className="h-5 w-5 text-primary" />
                <span className="text-lg font-semibold">Help & FAQ</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-6">
            {/* Demo Video */}
            <Card className="p-6">
              <Label className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Video className="h-5 w-5 text-primary" />
                Demo Video
              </Label>
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border-2 border-dashed">
                <div className="text-center">
                  <Video className="h-16 w-16 mx-auto text-muted-foreground mb-3" />
                  <p className="text-lg font-medium text-muted-foreground">Demo video coming soon</p>
                  <p className="text-sm text-muted-foreground mt-1">Learn how to use Wedding Lego</p>
                </div>
              </div>
            </Card>

            {/* FAQs */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index}>
                    <div className={`rounded-lg p-4 ${
                      faq.warning 
                        ? 'bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800' 
                        : 'bg-muted/30'
                    }`}>
                      <p className="font-semibold text-sm mb-2">{faq.question}</p>
                      <p className={`text-sm ${
                        faq.warning 
                          ? 'text-amber-900 dark:text-amber-100' 
                          : 'text-muted-foreground'
                      }`}>
                        {faq.answer}
                      </p>
                    </div>
                    {index < faqs.length - 1 && <Separator className="my-4" />}
                  </div>
                ))}
              </div>
            </Card>

            {/* Feedback Form */}
            <Card className="p-6">
              <Label className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Send Us Feedback
              </Label>
              <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                <Textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="Share your feedback, suggestions, or report issues...

We'd love to hear from you!"
                  rows={6}
                  className="resize-none"
                />
                <Button type="submit" className="w-full gradient-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Contact Info */}
            <Card className="p-6 bg-muted/30">
              <p className="text-sm text-muted-foreground text-center">
                Need immediate assistance? Email us at{' '}
                <a href="mailto:support@shaadibuddy.com" className="text-primary hover:underline font-medium">
                  support@shaadibuddy.com
                </a>
              </p>
            </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Terms & Conditions Section */}
          <AccordionItem value="terms" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <span className="text-lg font-semibold">Terms & Conditions</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
            <Card className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-2xl font-bold">Terms & Conditions</h2>
                  <p className="text-sm text-muted-foreground mt-1">Last Updated: {getTodayDate()}</p>
                </div>
              </div>

              <div className="prose prose-sm max-w-none space-y-6 text-sm">
                <p>
                  Welcome to <strong>Wedding Lego</strong>, operated by Mohit Mishra, Bengaluru, India ("we", "us", "our").
                  By accessing or using the Wedding Lego app or website ("Service"), you agree to these Terms & Conditions ("Terms").
                  If you do not agree, please do not use the Service.
                </p>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">1. Overview</h3>
                  <p>Wedding Lego is a wedding-planning management platform that allows users to:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Create or join an Organization</li>
                    <li>Manage multiple Weddings under an organization</li>
                    <li>Add Team Members with different roles</li>
                    <li>Create and manage Events, Tasks, Menus</li>
                    <li>Add and manage Guests, including accommodation details</li>
                    <li>Choose to manually send event/accommodation info to guests</li>
                  </ul>
                  <p className="mt-3">Wedding Lego provides workflow tools and does not provide wedding vendor guarantees or event execution services.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">2. Eligibility</h3>
                  <p>You must be at least 18 years old to use Wedding Lego. By using the Service, you represent that you meet this requirement.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">3. Account Registration</h3>
                  <p>When creating an account:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>You must provide accurate, complete information</li>
                    <li>You are responsible for maintaining the security of your account</li>
                    <li>You agree not to share login credentials with others</li>
                    <li>You must notify us immediately of unauthorized access</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">4. Organizations & Roles</h3>
                  <p>When creating an Organization:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>The creator becomes the Admin</li>
                    <li>Admins may invite or remove members</li>
                    <li>Admins may create weddings</li>
                    <li>Editors and Viewers have limited permissions</li>
                    <li>You must only add people to an Organization who consent to join</li>
                  </ul>
                  <p className="mt-3">You are responsible for all activity within your Organization.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">5. Weddings, Events, Guests & Data Accuracy</h3>
                  <p>You acknowledge that:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>All wedding details, event details, guest data, and accommodation information are entered by users</li>
                    <li>We do not verify the accuracy of user-submitted content</li>
                    <li>Users must obtain consent from guests before entering their personal or accommodation information</li>
                    <li>You are solely responsible for ensuring accuracy of all shared details</li>
                  </ul>
                  <p className="mt-3">Wedding Lego is a tool for organizing information — we do not manage real-world events.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">6. Manual Communication to Guests</h3>
                  <p>Wedding Lego allows you to manually send messages, emails, or notifications to guests.</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>No automated guest messaging is performed by the system</li>
                    <li>Messages are only sent when a user explicitly clicks a "Send" button</li>
                    <li>You are responsible for ensuring the correctness and legality of information shared with guests</li>
                    <li>We do not control the message content or guarantee message delivery</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">7. Acceptable Use</h3>
                  <p>You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Upload false, harmful, or illegal content</li>
                    <li>Harass, abuse, or impersonate others</li>
                    <li>Reverse engineer or disrupt the Service</li>
                    <li>Upload personal data without consent</li>
                    <li>Use the Service to spam or mass-message guests</li>
                  </ul>
                  <p className="mt-3">Violation may result in suspension or account termination.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">8. Payments & Subscriptions</h3>
                  <p>Some features may become part of paid subscription plans. We will inform users before charging for any feature. Subscription terms and refund policies will be provided separately when paid plans launch.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">9. Intellectual Property</h3>
                  <p>The Wedding Lego name, logo, UI, workflows, and design are owned by Mohit Mishra. You retain ownership of your uploaded content. You grant us a license to store and process your content only to operate the Service.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">10. Data Protection & Privacy</h3>
                  <p>Your use of Wedding Lego is also governed by our Privacy Policy, which explains how we collect and use your data.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">11. Limitation of Liability</h3>
                  <p>To the maximum extent permitted by law:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>We are not responsible for wedding delays, guest issues, vendor disputes, accommodation errors, or miscommunication</li>
                    <li>We do not guarantee that the Service will be uninterrupted or error-free</li>
                    <li>Your sole remedy is to stop using the Service</li>
                    <li>Our total liability shall not exceed any amount you paid (if any) in the previous 30 days</li>
                  </ul>
                  <p className="mt-3">Wedding Lego is a planning tool — all event execution responsibilities lie with users.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">12. Termination</h3>
                  <p>We may suspend or terminate accounts if:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Terms are violated</li>
                    <li>Illegal activity is detected</li>
                    <li>Fraudulent or abusive behavior occurs</li>
                  </ul>
                  <p className="mt-3">Users may delete their account at any time. Data deletion is handled as described in our Privacy Policy.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">13. Governing Law</h3>
                  <p>These Terms are governed by the laws of India. Jurisdiction: Bengaluru, Karnataka.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">14. Contact Us</h3>
                  <p>
                    For any questions, contact:{' '}
                    <a href="mailto:support@shaadibuddy.com" className="text-primary hover:underline">
                      support@shaadibuddy.com
                    </a>
                  </p>
                </section>
              </div>
            </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Privacy Policy Section */}
          <AccordionItem value="privacy" className="border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-lg font-semibold">Privacy Policy</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
            <Card className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-2xl font-bold">Privacy Policy</h2>
                  <p className="text-sm text-muted-foreground mt-1">Last Updated: {getTodayDate()}</p>
                </div>
              </div>

              <div className="prose prose-sm max-w-none space-y-6 text-sm">
                <p>
                  This Privacy Policy explains how <strong>Wedding Lego</strong> ("we", "our", "us") collects, uses, and protects your personal information.
                  By using our Service, you consent to this policy.
                </p>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">1. Information We Collect</h3>
                  <p>We may collect the following types of information:</p>
                  
                  <h4 className="font-semibold text-foreground mt-4 mb-2">A) Account Information</h4>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Name, Email, Phone number</li>
                    <li>Organization details</li>
                    <li>Profile picture (optional)</li>
                  </ul>

                  <h4 className="font-semibold text-foreground mt-4 mb-2">B) Wedding Information</h4>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Wedding names, dates, Event details</li>
                    <li>Task lists, Menu items</li>
                  </ul>

                  <h4 className="font-semibold text-foreground mt-4 mb-2">C) Guest Information</h4>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Guest name, Phone number, Email</li>
                    <li>Side (bride/groom)</li>
                    <li>Hotel name, address, Room number</li>
                    <li>Check-in / check-out dates</li>
                    <li>Event visibility preferences</li>
                    <li>Any message manually sent to them</li>
                  </ul>

                  <h4 className="font-semibold text-foreground mt-4 mb-2">D) Automatically Collected Data</h4>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>IP address, Device type, Browser type</li>
                    <li>Login time, Usage analytics</li>
                  </ul>

                  <h4 className="font-semibold text-foreground mt-4 mb-2">E) Cookies (Website Only)</h4>
                  <p>Used for: Login sessions, Analytics, Improving performance</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">2. How We Use Your Data</h3>
                  <p>We use data to:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Operate Organizations and Weddings</li>
                    <li>Provide event, guest, and task management features</li>
                    <li>Allow manual message sending to guests</li>
                    <li>Maintain security and prevent fraud</li>
                    <li>Improve app performance</li>
                    <li>Provide customer support</li>
                  </ul>
                  <p className="mt-3 font-semibold">We do not use your data for advertising.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">3. Guest Data Responsibility</h3>
                  <p>Because users can upload guest information, the responsibility is:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Users/organizations must obtain consent from guests before adding their data</li>
                    <li>Users are responsible for accuracy and safe handling of data they submit</li>
                    <li>We only process and store guest data on behalf of the organization</li>
                    <li>We are not responsible for unauthorized guest entries made by users</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">4. Manual Messaging Only</h3>
                  <p>Wedding Lego allows users to manually send messages to guests.</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>No automated sending</li>
                    <li>Users control both the timing and content</li>
                    <li>We process messages only when triggered by a user</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">5. Who We Share Data With</h3>
                  <p className="font-semibold">We do not sell your data.</p>
                  <p className="mt-2">We only share necessary information with:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Hosting providers</li>
                    <li>Email delivery providers</li>
                    <li>Analytics tools</li>
                    <li>Government authorities only when legally required</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">6. Data Storage & Security</h3>
                  <p>We use industry-standard security practices:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Encrypted data transmission (HTTPS)</li>
                    <li>Password hashing</li>
                    <li>Access controls</li>
                    <li>Regular vulnerability monitoring</li>
                  </ul>
                  <p className="mt-3">All data is stored on secure servers hosted in industry-grade cloud infrastructure.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">7. Your Rights</h3>
                  <p>You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>Access your data</li>
                    <li>Request correction</li>
                    <li>Request deletion</li>
                    <li>Export your data</li>
                    <li>Withdraw consent</li>
                    <li>Disable your account</li>
                    <li>Lodge complaints regarding misuse</li>
                  </ul>
                  <p className="mt-3">
                    Requests may be emailed to{' '}
                    <a href="mailto:support@shaadibuddy.com" className="text-primary hover:underline">
                      support@shaadibuddy.com
                    </a>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">8. Data Retention</h3>
                  <p>We retain data:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>As long as your account is active</li>
                    <li>Up to 90 days after account deletion</li>
                    <li>Backups may exist for an additional short period</li>
                  </ul>
                  <p className="mt-3">Guest data stored by users may remain until removed by the Organization Admin.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">9. Children's Privacy</h3>
                  <p>We do not knowingly collect data from anyone under 18.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">10. Changes to This Policy</h3>
                  <p>We may update this Privacy Policy from time to time. We will notify users through the app or email when significant changes occur.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-3">11. Contact Us</h3>
                  <p>
                    For questions or concerns:<br />
                    📧 <a href="mailto:support@shaadibuddy.com" className="text-primary hover:underline">support@shaadibuddy.com</a><br />
                    📍 Bengaluru, India
                  </p>
                </section>
              </div>
            </Card>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
